import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

import { degreesToRadians, percentToRadians } from './../helpers/getAngleConversions';

export interface MeterNeedleProps {
	meterPercent: number;
	needleHeight: number;
	needleRadius: number;
	position: { x: number; y: number };
}

/**
 * @param {number} meterPercent Percentage of meter from start that needle should point to.
 * @param {number} needleHeight Height of the needle.
 * @param {number} needleRadius How large the needle 'base' is. Also used to calculate size of the needle.
 * @param {{x: number, y: number}} position The relative position of this SVG g element
 *
 * @returns A SVG G element representing a needle pointing to a specified percentage of an arc.
 */
export const MeterNeedle = ({ meterPercent, needleHeight, needleRadius, position }: MeterNeedleProps) => {
	const ref = useRef<SVGGElement>(null);

	useEffect(() => {
		const needleOpacity = 0.85;

		/* Get spread of radians to get percentage of */
		const startRadians = degreesToRadians(270);
		const endRadians = degreesToRadians(450);
		const radianSpread = endRadians - startRadians;
		const spreadPercent = radianSpread / (2 * Math.PI);

		// - Math.PI / 2 will normalize thetaRad to d3 angle system where degree 0 is at top
		const thetaRad = percentToRadians(meterPercent * spreadPercent) + startRadians - Math.PI / 2;

		const needleTopX = needleHeight * Math.cos(thetaRad);
		const needleTopY = needleHeight * Math.sin(thetaRad);
		const needleLeftX = -needleRadius * Math.cos(thetaRad - Math.PI / 2);
		const needleLeftY = -needleRadius * Math.sin(thetaRad - Math.PI / 2);
		const needleRightX = needleRadius * Math.cos(thetaRad - Math.PI / 2);
		const needleRightY = needleRadius * Math.sin(thetaRad - Math.PI / 2);
		const path = [`M${needleLeftX},${needleLeftY}L${needleTopX},${needleTopY}L${needleRightX},${needleRightY}`];

		/* Needle Pointer */
		d3.select(ref.current)
			.selectAll('path')
			.data(path)
			.join(
				(enter) => enter.append('path').attr('d', path[0]).attr('opacity', needleOpacity),
				(update) => update.attr('d', path[0])
			);
	}, [meterPercent, needleHeight, needleRadius]);

	return (
		<g ref={ref} transform={`translate(${position.x}, ${position.y})`}>
			<circle r={needleRadius}></circle>
		</g>
	);
};
